import React, { forwardRef } from "react";

const PrintableExperienceCertificate = forwardRef(({ cert }, ref) => {
  if (!cert) return null;

  const formatDate = (dateVal) => {
    if (!dateVal) return "N/A";
    let str = String(dateVal).trim();
    if (!str || str.startsWith("0001-") || str.startsWith("1900-") || str === "N/A") return "N/A";

    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      const parts = str.split("T")[0].split("-");
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        if (year !== "0001" && year !== "1900") {
          return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
        }
      }
    }

    const d = new Date(str);
    if (isNaN(d.getTime())) return "N/A";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    if (year <= 1900) return "N/A";
    return `${day}/${month}/${year}`;
  };

  const empName = cert.employee?.name || cert.employee?.Name || "Employee Name";
  const empCode = cert.employee?.employeeCode || cert.employee?.EmployeeCode || "N/A";
  const deptName = cert.employee?.department?.name || cert.employee?.Department?.Name || "N/A";
  const designation = cert.designation || cert.employee?.designation || cert.employee?.Designation || "N/A";
  const joiningDate = formatDate(cert.joiningDate || cert.JoiningDate || cert.employee?.joiningDate);
  const relievingDate = formatDate(cert.relievingDate || cert.RelievingDate);
  const issueDate = formatDate(cert.issueDate || cert.IssueDate);

  return (
    <div
      ref={ref}
      className="w-[800px] bg-white p-12 text-gray-800 shadow-xl print:shadow-none border border-gray-200 print:border-none relative z-0 mt-4 mx-auto"
      style={{ fontFamily: "Arial, sans-serif" }}
    >
      {/* Background Watermark */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.07] flex items-center justify-center z-[-1]">
        <img
          src="/assets/images/logo.jpg"
          alt="Watermark"
          className="w-full h-fit object-cover"
        />
      </div>

      {/* Header with Logo */}
      <div className="flex items-center justify-center mb-8 pb-6 border-b-4 border-gray-800 relative z-10">
        <div className="text-center">
          <img
            src="/assets/images/logo.jpg"
            alt="School Logo"
            className="w-40 object-contain mx-auto -mt-16"
          />
          <div className="text-3xl font-black uppercase tracking-widest mb-1 mt-4">
            DEE NOVO SCHOOL
          </div>
          <div className="text-sm font-semibold uppercase tracking-widest text-gray-600">
            Affiliated to CBSE • Dumka, Jharkhand
          </div>
          <div className="text-xl font-black uppercase tracking-[0.2em] mt-6 border-4 border-gray-800 inline-block px-8 py-2 rounded-full">
            EXPERIENCE CERTIFICATE
          </div>
        </div>
      </div>

      <div className="flex justify-between mb-8 text-sm font-bold uppercase relative z-10">
        <div>
          Employee Code:{" "}
          <span className="border-b border-dashed border-gray-400 px-4 min-w-[150px] inline-block mb-1">
            {empCode}
          </span>
        </div>
        <div>
          Cert No:{" "}
          <span className="border-b border-dashed border-gray-400 px-4 min-w-[100px] inline-block text-blue-700 mb-1">
            {cert.certificateNumber || "EXP-DRAFT"}
          </span>
        </div>
      </div>

      {/* Certificate Body Paragraph */}
      <div className="mt-8 text-base leading-relaxed text-justify relative z-10 space-y-4">
        <p className="indent-8 font-medium">
          This is to certify that <span className="font-black text-lg uppercase">{empName}</span> served as a{" "}
          <span className="font-bold uppercase">{designation}</span> {deptName !== "N/A" ? `in the Department of ${deptName}` : ""} at Dee Novo School from{" "}
          <span className="font-bold">{joiningDate}</span> to <span className="font-bold">{relievingDate}</span>.
        </p>

        <p className="indent-8 font-medium">
          During their period of service with our institution, they demonstrated high professionalism, dedication, and sincere effort toward their duties. Their conduct and character were found to be <span className="font-bold uppercase">{cert.conduct || "GOOD"}</span>.
        </p>

        {cert.reasonForLeaving && (
          <p className="indent-8 font-medium">
            <span className="font-bold uppercase">Reason for Relieving:</span> {cert.reasonForLeaving}.
          </p>
        )}

        {cert.remarks && (
          <p className="indent-8 font-medium">
            <span className="font-bold uppercase">Remarks:</span> {cert.remarks}.
          </p>
        )}

        <p className="indent-8 font-medium pt-2">
          We express our gratitude for their valuable services and wish them every success in all future professional endeavors.
        </p>
      </div>

      <div className="mt-8 text-sm font-bold uppercase relative z-10">
        <div>
          Date of Issue: <span className="font-normal">{issueDate}</span>
        </div>
      </div>

      {/* Signatures */}
      <div className="flex justify-between items-end mt-28 mx-4 font-bold text-sm uppercase relative z-10">
        <div className="text-center">
          <div className="w-48 border-t-2 border-gray-800 pt-2">
            Prepared By
          </div>
        </div>
        <div className="text-center">
          <div className="w-48 border-t-2 border-gray-800 pt-2">
            Administrative Officer
          </div>
        </div>
        <div className="text-center">
          <div className="w-48 border-t-2 border-gray-800 pt-2">
            Principal / Director
          </div>
        </div>
      </div>
    </div>
  );
});

export default PrintableExperienceCertificate;
